import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, ApiRequestError } from '../api/client.js';
import type { Investigation, WorkOrderDetail } from '../api/types.js';
import { useSession } from '../auth/SessionProvider.js';
import { PERMISSIONS } from '../../shared/permissions.js';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  InternalPanel,
  Spinner,
  TextArea,
  TextInput,
} from '../components/ui.js';
import { formatDate, formatDateTime } from '../lib/format.js';

export function InvestigationSection({ workOrder }: { workOrder: WorkOrderDetail }) {
  const queryClient = useQueryClient();
  const { can, isContractor } = useSession();
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mayManage =
    isContractor &&
    can(PERMISSIONS.INVESTIGATION_MANAGE) &&
    !['closed', 'cancelled'].includes(workOrder.status);

  const { data, isLoading } = useQuery({
    queryKey: ['investigation', workOrder.id],
    queryFn: () =>
      apiRequest<{ investigation: Investigation | null }>(
        `/api/work-orders/${workOrder.id}/investigation`,
      ).then((result) => result.investigation),
  });

  const [form, setForm] = useState({
    inspectedOn: '',
    findings: '',
    rootCause: '',
    recommendedAction: '',
    notesInternal: '',
    estimatedLaborHours: '',
    requiresSubcontractor: false,
  });

  useEffect(() => {
    if (!data) return;
    setForm({
      inspectedOn: data.inspectedOn ?? '',
      findings: data.findings ?? '',
      rootCause: data.rootCause ?? '',
      recommendedAction: data.recommendedAction ?? '',
      notesInternal: data.notesInternal ?? '',
      estimatedLaborHours: data.estimatedLaborHours ?? '',
      requiresSubcontractor: data.requiresSubcontractor ?? false,
    });
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      apiRequest(`/api/work-orders/${workOrder.id}/investigation`, {
        method: 'PUT',
        body: {
          inspectedOn: form.inspectedOn || undefined,
          findingsCustomerVisible: form.findings.trim() || undefined,
          rootCauseCustomerVisible: form.rootCause.trim() || undefined,
          recommendedActionCustomerVisible: form.recommendedAction.trim() || undefined,
          notesInternal: form.notesInternal.trim() || undefined,
          estimatedLaborHours: form.estimatedLaborHours.trim() || undefined,
          requiresSubcontractor: form.requiresSubcontractor,
        },
      }),
    onSuccess: () => {
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: ['investigation', workOrder.id] });
      void queryClient.invalidateQueries({ queryKey: ['work-order', workOrder.id] });
      void queryClient.invalidateQueries({ queryKey: ['timeline', workOrder.id] });
    },
    onError: (caught: unknown) => {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'The findings could not be saved. Please try again.',
      );
    },
  });

  if (isLoading) return <Spinner label="Loading investigation" />;

  if (editing) {
    return (
      <Card title="Investigation findings">
        <div className="flex flex-col gap-4">
          {error && <Alert variant="error">{error}</Alert>}
          <Alert variant="info">
            Findings, root cause, and recommended action are shown to USPS. Internal notes are not.
          </Alert>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Date inspected" htmlFor="inspectedOn">
              <TextInput
                id="inspectedOn"
                type="date"
                value={form.inspectedOn}
                onChange={(event) => setForm({ ...form, inspectedOn: event.target.value })}
              />
            </Field>
            <Field label="Estimated labor hours" htmlFor="estimatedLaborHours">
              <TextInput
                id="estimatedLaborHours"
                inputMode="decimal"
                placeholder="8.5"
                value={form.estimatedLaborHours}
                onChange={(event) => setForm({ ...form, estimatedLaborHours: event.target.value })}
              />
            </Field>
          </div>

          <Field label="Findings" htmlFor="findings" hint="Visible to USPS.">
            <TextArea
              id="findings"
              rows={5}
              value={form.findings}
              onChange={(event) => setForm({ ...form, findings: event.target.value })}
            />
          </Field>

          <Field label="Root cause" htmlFor="rootCause" hint="Visible to USPS.">
            <TextArea
              id="rootCause"
              rows={3}
              value={form.rootCause}
              onChange={(event) => setForm({ ...form, rootCause: event.target.value })}
            />
          </Field>

          <Field label="Recommended action" htmlFor="recommendedAction" hint="Visible to USPS.">
            <TextArea
              id="recommendedAction"
              rows={4}
              value={form.recommendedAction}
              onChange={(event) => setForm({ ...form, recommendedAction: event.target.value })}
            />
          </Field>

          <label className="flex items-center gap-2 text-sm text-slate-800">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-brand-700 focus:ring-brand-600"
              checked={form.requiresSubcontractor}
              onChange={(event) =>
                setForm({ ...form, requiresSubcontractor: event.target.checked })
              }
            />
            A subcontractor is required for this repair
          </label>

          <InternalPanel title="VGS internal notes">
            <TextArea
              aria-label="VGS internal notes"
              rows={4}
              value={form.notesInternal}
              onChange={(event) => setForm({ ...form, notesInternal: event.target.value })}
            />
          </InternalPanel>

          <div className="flex gap-2">
            <Button onClick={() => save.mutate()} loading={save.isPending}>
              Save findings
            </Button>
            <Button variant="secondary" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title="Investigation findings"
      actions={
        mayManage && (
          <Button variant="secondary" onClick={() => setEditing(true)}>
            {data ? 'Edit findings' : 'Record findings'}
          </Button>
        )
      }
    >
      {!data ? (
        <EmptyState
          title="No findings recorded yet"
          description={
            isContractor
              ? 'Record what was found on site so a proposal can be prepared.'
              : 'VGS will post findings after inspecting the reported problem.'
          }
        />
      ) : (
        <div className="flex flex-col gap-4">
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Date inspected
              </dt>
              <dd className="text-sm text-slate-900">{formatDate(data.inspectedOn)}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Last updated
              </dt>
              <dd className="text-sm text-slate-900">{formatDateTime(data.updatedAt)}</dd>
            </div>
          </dl>

          <TextBlock label="Findings" value={data.findings} />
          <TextBlock label="Root cause" value={data.rootCause} />
          <TextBlock label="Recommended action" value={data.recommendedAction} />

          {data.notesInternal !== undefined && (
            <InternalPanel title="VGS internal notes">
              <p className="whitespace-pre-wrap text-sm text-slate-800">
                {data.notesInternal ?? 'None recorded.'}
              </p>
              {data.estimatedLaborHours && (
                <p className="mt-2 text-sm text-slate-700">
                  Estimated labor: {data.estimatedLaborHours} hours
                  {data.requiresSubcontractor ? ' · subcontractor required' : ''}
                </p>
              )}
            </InternalPanel>
          )}
        </div>
      )}
    </Card>
  );
}

function TextBlock({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</h3>
      <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800">{value}</p>
    </div>
  );
}
